import React, { createContext, useContext, ReactNode } from 'react';
import { useWebSocketConnection } from './hooks';

interface WebSocketContextValue {
  isConnected: boolean;
  error: Error | null;
  reconnect: () => void;
}

const WebSocketContext = createContext<WebSocketContextValue | null>(null);

interface WebSocketProviderProps {
  children: ReactNode;
  tenantUuid?: string | null;
}

/**
 * WebSocket provider component that manages the global WebSocket connection.
 * Only connects when tenantUuid is provided.
 *
 * Usage:
 * ```tsx
 * <WebSocketProvider tenantUuid={tenant?.uuid}>
 *   <App />
 * </WebSocketProvider>
 * ```
 */
export function WebSocketProvider({ children, tenantUuid }: WebSocketProviderProps) {
  const { isConnected, error, reconnect } = useWebSocketConnection(tenantUuid);

  return (
    <WebSocketContext.Provider value={{ isConnected, error, reconnect }}>
      {children}
    </WebSocketContext.Provider>
  );
}

/**
 * Hook to access WebSocket connection state
 */
export function useWebSocket(): WebSocketContextValue {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
}
